import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import isIP from 'validator/lib/isIP'

import { DataTable } from 'components/DataTable'
import { Form, Text, Select, Checkbox } from 'components/forms'
import Button from 'components/Button'
import { notEmpty } from 'helpers/validators'
import { useMutation } from 'components/graphql'
import updateShop from '../../gql/updateShop'
import getShop from '../../gql/getShop'
import { GruberContainer, GruberTableWrapper, GruberPanel, GruberPanelTitle, GruberPanelButtons } from './styled'

const columns = t => [
	{ accessor: 'name', Header: t('options_section.fields.gruber_name') },
	{ accessor: 'ip', Header: t('options_section.fields.gruber_ip') },
	{ accessor: 'crName', Header: t('options_section.fields.gruber_master_cr') },
	{ accessor: 'pooling', Header: t('options_section.fields.gruber_pooling'), Cell: ({ value }) => value ? t('common.strings.yes') : t('common.strings.no') },
]

// eslint-disable-next-line no-unused-vars
const stripTypename = items => items.map(({ __typename, ...item }) => item)

const GruberDispenserConfig = ({ shopId, gruberConfig = [], shopCashRegisterList = [], loading }) => {
	const [t] = useTranslation()
	const [editItem, setEditItem] = useState(null)
	const [updateShopMutation] = useMutation(updateShop)

	const crMap = Object.fromEntries(shopCashRegisterList.map(({ id, name }) => [String(id), name]))

	const tableData = gruberConfig.map(item => ({
		...item,
		crName: crMap[String(item.crId)] || ''
	}))

	const save = items => updateShopMutation({
		variables: { id: shopId, fields: { gruberConfig: stripTypename(items) } },
		awaitRefetchQueries: true,
		refetchQueries: [{ query: getShop, variables: { id: shopId } }]
	})

	const handleSubmit = vals => {
		const existingId = editItem?.id
		const entry = {
			id: existingId || crypto.randomUUID(),
			name: vals.name,
			ip: vals.ip,
			crId: Number(vals.crId),
			pooling: !!vals.pooling
		}
		save(existingId
			? gruberConfig.map(item => item.id === existingId ? entry : item)
			: [...gruberConfig, entry]
		)
		setEditItem(null)
	}

	const handleDelete = id => save(gruberConfig.filter(item => item.id !== id))

	return(
		<GruberContainer>
			<GruberTableWrapper>
				<DataTable
					data={tableData}
					loading={loading}
					columns={columns(t)}
					onAddRecord={() => setEditItem({})}
					mutateRow={{
						editBtn: false,
						onRowMutate: id => typeof id === 'string' && handleDelete(id)
					}}
					selectRow={{
						selected: editItem?.id,
						setSelectedRecord: id => setEditItem(gruberConfig.find(i => i.id === id) || null)
					}}
				/>
			</GruberTableWrapper>
			{editItem !== null && (
				<GruberPanel>
					<GruberPanelTitle>
						{editItem?.id ? editItem.name : t('common.strings.new_record')}
					</GruberPanelTitle>
					<Form initialValues={editItem} onSubmit={handleSubmit}>
						<Text
							field='name'
							label={t('options_section.fields.gruber_name')}
							validate={v => {
								if(!v) {
									return t('validators.required')
								}
								const isDuplicate = gruberConfig.some(i => i.id !== editItem?.id && i.name === v)
								if(isDuplicate) {
									return t('validators.duplicate')
								}
							}}
							validateOnChange
						/>
						<Text
							field='ip'
							label={t('options_section.fields.gruber_ip')}
							{...notEmpty()}
							validate={v => {
								if(!v) {
									return t('validators.required')
								}
								if(!isIP(v)) {
									return t('validators.not_a_valid_ip_address')
								}
							}}
							validateOnChange
						/>
						<Select
							field='crId'
							label={t('options_section.fields.gruber_master_cr')}
							options={shopCashRegisterList}
							valueField='id'
							labelField='name'
							isSearchable
							isClearable
							{...notEmpty()}
							validateOnChange
						/>
						<Checkbox field='pooling' label={t('options_section.fields.gruber_pooling_activate')} />
						<GruberPanelButtons>
							<Button type='submit' text={t('common.buttons.save')} />
							<Button type='button' text={t('common.buttons.abort')} onClick={() => setEditItem(null)} status='cancelation' />
						</GruberPanelButtons>
					</Form>
				</GruberPanel>
			)}
		</GruberContainer>
	)
}

export default GruberDispenserConfig
